import {
  DestroyRef,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { BusEvent } from './bus-event';
import { EventDispatcher } from './event-dispatcher';

/**
 * Internal: wire a read-only consumer to the dispatcher's ordered `events$`
 * stream, with teardown tied to the environment injector's `DestroyRef`.
 *
 * `makeHandler` runs inside the initializer's injection context (so it may
 * `inject(...)`) and returns the per-event handler, or `null` to opt out of
 * subscribing at all. The subscription is read-only: it never dispatches and
 * never `error`/`complete`s the immortal root `Subject`; unsubscribing only
 * detaches this consumer and never touches the subject.
 *
 * The observability taps (`provideEventBusDevLogger`, `provideEventBusAnalytics`)
 * are thin wrappers over this — they differ only in how they decide whether to
 * subscribe and what they do per event.
 */
export function provideEventTap(
  makeHandler: () => ((event: BusEvent) => void) | null,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      const handler = makeHandler();
      if (handler === null) {
        return;
      }
      const dispatcher = inject(EventDispatcher);
      const destroyRef = inject(DestroyRef);
      dispatcher.events$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(handler);
    }),
  ]);
}
